/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        35+ Production Dynamic Prompt Templates Catalog (JSON Schema Driven)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import type { PromptCategoryDto, PromptTemplateDto } from '@sofia/shared-types';


/**
 * Core Prompt Categories.
 */
export const PROMPT_CATEGORIES_CATALOG: PromptCategoryDto[] = [
  {
    id: 'pcat_blog',
    name: 'Blog & Longform Articles',
    slug: 'blog-longform',
    icon: 'article',
    sortOrder: 10,
  },
  {
    id: 'pcat_marketing',
    name: 'Ads & Social Marketing',
    slug: 'marketing-ads',
    icon: 'brand-facebook',
    sortOrder: 20,
  },
  {
    id: 'pcat_email',
    name: 'Email Marketing & Outreach',
    slug: 'email-marketing',
    icon: 'mail',
    sortOrder: 30,
  },
  {
    id: 'pcat_ecommerce',
    name: 'E-Commerce & Product Copy',
    slug: 'ecommerce',
    icon: 'shopping-cart',
    sortOrder: 40,
  },
  {
    id: 'pcat_coding',
    name: 'Coding & Developer Tools',
    slug: 'coding-technical',
    icon: 'code',
    sortOrder: 50,
  },
];

/**
 * 35+ Pre-configured, high-converting AI Prompt Templates.
 */
export const PROMPT_TEMPLATES_CATALOG: Omit<PromptTemplateDto, 'category'>[] = [
  // =========================================================================
  // GROUP 1: Blog & Longform Content (8 Templates)
  // =========================================================================
  {
    id: 'ptpl_blog_post_ideas',
    categoryId: 'pcat_blog',
    title: 'Blog Post Ideas Generator',
    slug: 'blog-post-ideas',
    description: 'Generates 10 high-potential, creative blog topic ideas tailored to your niche and audience.',
    systemPrompt: 'You are an elite content strategist and SEO researcher. Generate compelling, viral-ready blog post ideas with search intent analysis.',
    promptTemplate: 'Please generate 10 unique blog post ideas for the following niche:\n\nNiche: {{niche}}\nTarget Audience: {{audience}}\nDesired Angle/Goal: {{goal | default: "Educational and thought leadership"}}',
    inputSchema: [
      { name: 'niche', label: 'Industry or Niche', type: 'text', placeholder: 'e.g. B2B SaaS Cloud Infrastructure', required: true },
      { name: 'audience', label: 'Target Audience', type: 'text', placeholder: 'e.g. CTOs and DevOps Engineers', required: true },
      { name: 'goal', label: 'Content Goal', type: 'select', options: ['Educational and thought leadership', 'Lead generation', 'Brand awareness', 'SEO keyword ranking'], defaultValue: 'Educational and thought leadership', required: false },
    ],
    defaultModelId: 'mod_gpt_4o',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_blog_title',
    categoryId: 'pcat_blog',
    title: 'High-CTR Blog Titles',
    slug: 'blog-title',
    description: 'Creates 10 irresistibly clickable and SEO-friendly headlines that drive traffic.',
    systemPrompt: 'You are a master copywriter specializing in editorial headlines and CTR optimization.',
    promptTemplate: 'Generate 10 magnetic blog titles for an article about:\n\nTopic: {{topic}}\nPrimary Keyword: {{keyword}}\nTone: {{tone | default: "Catchy & Informative"}}',
    inputSchema: [
      { name: 'topic', label: 'Article Topic', type: 'text', placeholder: 'e.g. Why Edge Computing Replaces Traditional Servers', required: true },
      { name: 'keyword', label: 'Primary Focus Keyword', type: 'text', placeholder: 'e.g. Edge Computing Benefits', required: true },
      { name: 'tone', label: 'Title Tone', type: 'select', options: ['Catchy & Informative', 'How-to / Guide', 'Listicle / Numbers', 'Controversial / Thought-Provoking'], defaultValue: 'Catchy & Informative', required: false },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_blog_outline',
    categoryId: 'pcat_blog',
    title: 'Comprehensive Blog Outline',
    slug: 'blog-outline',
    description: 'Constructs an organized, hierarchical H2 and H3 outline ensuring comprehensive topic coverage.',
    systemPrompt: 'You are a professional editorial director. Organize complex ideas into logical, well-structured outlines adhering to Google E-E-A-T.',
    promptTemplate: 'Create an in-depth, structured outline for the following blog post:\n\nTitle/Topic: {{title}}\nTarget Word Count: {{word_count | default: 1500}} words\nKey Subtopics to Cover: {{subtopics}}',
    inputSchema: [
      { name: 'title', label: 'Article Title or Topic', type: 'text', placeholder: 'e.g. Complete Guide to Cloudflare Workers and D1 SQLite', required: true },
      { name: 'word_count', label: 'Target Word Count', type: 'number', defaultValue: 1500, required: false },
      { name: 'subtopics', label: 'Key Points or Subtopics to Include', type: 'textarea', placeholder: 'e.g. Cost comparison, Edge latency, Drizzle ORM setup', required: false },
    ],
    defaultModelId: 'mod_gpt_4o',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_section_expander',
    categoryId: 'pcat_blog',
    title: 'Section Expander',
    slug: 'section-expander',
    description: 'Expands a single outline bullet point or brief note into a richly detailed, informative section.',
    systemPrompt: 'You are a skilled longform journalist. Expand brief outlines into compelling, articulate paragraphs filled with substance.',
    promptTemplate: 'Please write a comprehensive, engaging section based on this heading:\n\nSection Heading: {{heading}}\nKey Context/Data: {{context}}\nStyle: {{style | default: "Analytical and practical"}}',
    inputSchema: [
      { name: 'heading', label: 'Section Heading (H2/H3)', type: 'text', placeholder: 'e.g. Sub-millisecond TTFT on Global Edge Workers', required: true },
      { name: 'context', label: 'Key Points or Facts to Include', type: 'textarea', placeholder: 'e.g. V8 isolate startup in 0ms, distributed over 300+ cities', required: true },
      { name: 'style', label: 'Writing Style', type: 'select', options: ['Analytical and practical', 'Storytelling and engaging', 'Academic and formal'], defaultValue: 'Analytical and practical', required: false },
    ],
    defaultModelId: 'mod_claude_35_sonnet',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_content_summarizer',
    categoryId: 'pcat_blog',
    title: 'Executive Content Summarizer',
    slug: 'content-summarizer',
    description: 'Condenses extensive articles, whitepapers, or transcripts into concise bullet points and takeaways.',
    systemPrompt: 'You are an executive research assistant. Distill complex information into high-signal, zero-fluff summaries.',
    promptTemplate: 'Please summarize the following content into {{bullet_count | default: 5}} key takeaways:\n\nFormat: {{format | default: "Key Bullet Points"}}\n\nContent:\n{{content}}',
    inputSchema: [
      { name: 'content', label: 'Source Text to Summarize', type: 'textarea', placeholder: 'Paste your long text here...', required: true },
      { name: 'bullet_count', label: 'Number of Takeaways', type: 'number', defaultValue: 5, required: false },
      { name: 'format', label: 'Summary Format', type: 'select', options: ['Key Bullet Points', 'One-Paragraph TL;DR', 'Actionable Checklist'], defaultValue: 'Key Bullet Points', required: false },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_paragraph_generator',
    categoryId: 'pcat_blog',
    title: 'Paragraph Generator',
    slug: 'paragraph-generator',
    description: 'Generates cohesive, well-reasoned paragraphs focusing on a specific argument or idea.',
    systemPrompt: 'You are a master essayist and copy editor. Write clear, persuasive paragraphs with seamless transitions.',
    promptTemplate: 'Write {{count | default: 2}} well-crafted paragraphs about:\n\nMain Idea: {{main_idea}}\nSupporting Details: {{details}}\nTone: {{tone | default: "Professional"}}',
    inputSchema: [
      { name: 'main_idea', label: 'Main Idea or Argument', type: 'text', placeholder: 'e.g. Why serverless databases reduce maintenance overhead', required: true },
      { name: 'details', label: 'Supporting Details / Evidence', type: 'textarea', placeholder: 'e.g. Automatic scaling, zero server patching, pay per query', required: false },
      { name: 'count', label: 'Paragraph Count', type: 'number', defaultValue: 2, required: false },
      { name: 'tone', label: 'Tone', type: 'select', options: ['Professional', 'Casual', 'Persuasive', 'Inspirational'], defaultValue: 'Professional', required: false },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_article_intro',
    categoryId: 'pcat_blog',
    title: 'Hook & Article Introduction',
    slug: 'article-introduction',
    description: 'Hooks the reader immediately and sets the stage for a compelling longform article.',
    systemPrompt: 'You are an award-winning content writer. Craft irresistible article openers that captivate reader curiosity.',
    promptTemplate: 'Write an irresistible article introduction for:\n\nTitle: {{title}}\nFormula: {{formula | default: "Problem-Agitate-Solve (PAS)"}}\nTarget Reader: {{reader}}',
    inputSchema: [
      { name: 'title', label: 'Article Title', type: 'text', placeholder: 'e.g. The Hidden Cloud Costs Killing Early-Stage Startups', required: true },
      { name: 'formula', label: 'Introduction Formula', type: 'select', options: ['Problem-Agitate-Solve (PAS)', 'Provocative Question & Hook', 'Statistical Shock & Insight', 'Story / Anecdote'], defaultValue: 'Problem-Agitate-Solve (PAS)', required: false },
      { name: 'reader', label: 'Target Reader', type: 'text', placeholder: 'e.g. Bootstrapped Startup Founders', required: false },
    ],
    defaultModelId: 'mod_gpt_4o',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_article_conclusion',
    categoryId: 'pcat_blog',
    title: 'Article Conclusion & CTA',
    slug: 'article-conclusion',
    description: 'Brings your post to a powerful finish with key wrap-ups and a strong Call to Action (CTA).',
    systemPrompt: 'You are a seasoned editorial writer. Summarize key takeaways and craft inspiring closing statements.',
    promptTemplate: 'Write a memorable conclusion for an article about {{topic}}.\n\nKey Message to Reinforce: {{key_message}}\nCall to Action (CTA): {{cta}}',
    inputSchema: [
      { name: 'topic', label: 'Article Topic', type: 'text', placeholder: 'e.g. Migrating from Node.js monolith to Cloudflare Workers', required: true },
      { name: 'key_message', label: 'Final Takeaway Message', type: 'text', placeholder: 'e.g. Modernizing infrastructure pays off in latency and cost savings', required: true },
      { name: 'cta', label: 'Call to Action', type: 'text', placeholder: 'e.g. Try Sophia Platform free for 14 days', required: false },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },

  // =========================================================================
  // GROUP 2: Ads & Marketing Copywriting (10 Templates)
  // =========================================================================
  {
    id: 'ptpl_facebook_ad_primary',
    categoryId: 'pcat_marketing',
    title: 'Facebook Ad Primary Text',
    slug: 'facebook-ad-primary-text',
    description: 'Generates high-converting Facebook/Instagram ad copy using proven direct-response frameworks.',
    systemPrompt: 'You are an expert direct-response media buyer and Facebook ad copywriter.',
    promptTemplate: 'Write 3 variations of Facebook Ad primary text for:\n\nProduct: {{product}}\nTarget Audience: {{audience}}\nCore Benefit: {{benefit}}\nFramework: {{framework | default: "PAS (Problem - Agitate - Solve)"}}',
    inputSchema: [
      { name: 'product', label: 'Product or Service Name', type: 'text', placeholder: 'e.g. Sophia AI Edge Platform', required: true },
      { name: 'audience', label: 'Target Audience', type: 'text', placeholder: 'e.g. Content Creators & Digital Marketers', required: true },
      { name: 'benefit', label: 'Primary Benefit / USP', type: 'text', placeholder: 'e.g. Cut content creation time by 80% with native edge streaming', required: true },
      { name: 'framework', label: 'Copywriting Framework', type: 'select', options: ['PAS (Problem - Agitate - Solve)', 'AIDA (Attention - Interest - Desire - Action)', 'BAB (Before - After - Bridge)'], defaultValue: 'PAS (Problem - Agitate - Solve)', required: false },
    ],
    defaultModelId: 'mod_gpt_4o',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_facebook_ad_headline',
    categoryId: 'pcat_marketing',
    title: 'Facebook Ad Headlines',
    slug: 'facebook-ad-headline',
    description: 'Generates 10 punchy, thumb-stopping headlines for Facebook and Meta carousel ads.',
    systemPrompt: 'You are a performance marketing specialist. Write short, punchy Facebook ad headlines under 40 characters.',
    promptTemplate: 'Generate 10 high-converting Facebook ad headlines for:\n\nOffer: {{offer}}\nAudience: {{audience}}\nAngle: {{angle | default: "Curiosity & Urgency"}}',
    inputSchema: [
      { name: 'offer', label: 'Core Offer or Product', type: 'text', placeholder: 'e.g. $0 Cloudflare Edge-Native AI Workspace', required: true },
      { name: 'audience', label: 'Audience', type: 'text', placeholder: 'e.g. Tech Founders', required: false },
      { name: 'angle', label: 'Headline Angle', type: 'select', options: ['Curiosity & Urgency', 'Direct Benefit', 'Social Proof', 'Discount / Deal'], defaultValue: 'Curiosity & Urgency', required: false },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_google_search_ad',
    categoryId: 'pcat_marketing',
    title: 'Google Search Ads (RSA)',
    slug: 'google-search-ad',
    description: 'Creates Responsive Search Ads with 15 headlines (<=30 chars) and 4 descriptions (<=90 chars).',
    systemPrompt: 'You are a certified Google Ads Specialist. Adhere strictly to character counts: Headlines <= 30 characters, Descriptions <= 90 characters.',
    promptTemplate: 'Generate Google Responsive Search Ads (10 headlines <=30 chars, 4 descriptions <=90 chars) for:\n\nBusiness: {{business}}\nTarget Keywords: {{keywords}}\nCall to Action: {{cta | default: "Get Started Free"}}',
    inputSchema: [
      { name: 'business', label: 'Business / Product Description', type: 'text', placeholder: 'e.g. Sophia AI Content Creation Suite', required: true },
      { name: 'keywords', label: 'Target Search Keywords', type: 'text', placeholder: 'e.g. ai writer, prompt generator, edge computing', required: true },
      { name: 'cta', label: 'Primary Call to Action', type: 'text', defaultValue: 'Get Started Free', required: false },
    ],
    defaultModelId: 'mod_gpt_4o',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_instagram_caption',
    categoryId: 'pcat_marketing',
    title: 'Instagram Post Caption',
    slug: 'instagram-caption',
    description: 'Generates engaging Instagram captions complete with line breaks, emojis, and a call to action.',
    systemPrompt: 'You are an Instagram growth strategist. Write engaging captions with line spacing, tasteful emojis, and strong engagement hooks.',
    promptTemplate: 'Write an Instagram caption for a post about:\n\nPhoto/Post Subject: {{subject}}\nGoal: {{goal | default: "Get comments and saves"}}\nTone: {{tone | default: "Relatable and Inspiring"}}',
    inputSchema: [
      { name: 'subject', label: 'Post Subject or Image Context', type: 'textarea', placeholder: 'e.g. Behind the scenes building our new AI feature', required: true },
      { name: 'goal', label: 'Engagement Goal', type: 'select', options: ['Get comments and saves', 'Drive traffic to bio link', 'Share a tip / value bomb'], defaultValue: 'Get comments and saves', required: false },
      { name: 'tone', label: 'Tone', type: 'select', options: ['Relatable and Inspiring', 'Humorous & Casual', 'Educational & Authoritative'], defaultValue: 'Relatable and Inspiring', required: false },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_instagram_hashtags',
    categoryId: 'pcat_marketing',
    title: 'Instagram Hashtags Generator',
    slug: 'instagram-hashtags',
    description: 'Recommends 30 categorized hashtags (high, medium, and low competition) to maximize reach.',
    systemPrompt: 'You are a social media SEO expert. Provide targeted hashtags organized by volume and competition tiers.',
    promptTemplate: 'Generate 30 strategic Instagram hashtags for:\n\nNiche/Topic: {{topic}}\nTarget Location/Language: {{location | default: "Global / English"}}',
    inputSchema: [
      { name: 'topic', label: 'Topic or Industry', type: 'text', placeholder: 'e.g. Digital Marketing & AI Automation', required: true },
      { name: 'location', label: 'Target Market / Language', type: 'text', defaultValue: 'Global / English', required: false },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_linkedin_post',
    categoryId: 'pcat_marketing',
    title: 'LinkedIn Thought Leadership',
    slug: 'linkedin-thought-leadership',
    description: 'Crafts professional, thought-provoking LinkedIn posts that establish industry credibility.',
    systemPrompt: 'You are an executive ghostwriter for top tech founders and venture capitalists on LinkedIn.',
    promptTemplate: 'Write an impactful LinkedIn post about:\n\nCore Insight: {{insight}}\nLesson or Takeaway: {{lesson}}\nFormat: {{format | default: "Short punchy lines with line breaks"}}',
    inputSchema: [
      { name: 'insight', label: 'Core Insight or Contrarian Opinion', type: 'textarea', placeholder: 'e.g. Why monoliths are dying and edge architecture is winning', required: true },
      { name: 'lesson', label: 'Actionable Lesson Learned', type: 'text', placeholder: 'e.g. Speed of execution and latency directly dictate user retention', required: true },
      { name: 'format', label: 'Post Format', type: 'select', options: ['Short punchy lines with line breaks', 'Story with a moral lesson', 'Framework / Step-by-step breakdown'], defaultValue: 'Short punchy lines with line breaks', required: false },
    ],
    defaultModelId: 'mod_claude_35_sonnet',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_twitter_thread',
    categoryId: 'pcat_marketing',
    title: 'Viral Twitter / X Thread',
    slug: 'twitter-thread',
    description: 'Produces a multi-tweet thread with a high-retention hook, value-packed body, and retweet CTA.',
    systemPrompt: 'You are a viral X/Twitter growth consultant. Master the art of the 280-character tweet hook and narrative momentum.',
    promptTemplate: 'Create an engaging 7-tweet thread on:\n\nTopic: {{topic}}\nKey Data or Points: {{points}}\nEnding CTA: {{cta | default: "Follow and retweet if you found this helpful"}}',
    inputSchema: [
      { name: 'topic', label: 'Thread Topic', type: 'text', placeholder: 'e.g. How we migrated 50,000 users to Cloudflare D1 for $5/mo', required: true },
      { name: 'points', label: 'Key Insights / Evidence to include', type: 'textarea', placeholder: 'e.g. MySQL bottlenecks, Drizzle ORM batching, 0ms cold starts', required: true },
      { name: 'cta', label: 'Ending Tweet CTA', type: 'text', defaultValue: 'Follow and retweet if you found this helpful', required: false },
    ],
    defaultModelId: 'mod_gpt_4o',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_tiktok_script',
    categoryId: 'pcat_marketing',
    title: 'TikTok / Reel Video Script',
    slug: 'tiktok-video-script',
    description: 'Writes a dynamic 30-60 second short-form video script with visual cues and a 3-second hook.',
    systemPrompt: 'You are a short-form video director. Format scripts clearly with [Visual / Scene Direction] and (Spoken Narration).',
    promptTemplate: 'Write a 45-second TikTok/Shorts script about:\n\nConcept: {{concept}}\nHook Style: {{hook_style | default: "Shocking Fact / Curiosity"}}',
    inputSchema: [
      { name: 'concept', label: 'Video Concept or Product', type: 'text', placeholder: 'e.g. Generating a full article in 3 seconds on your phone', required: true },
      { name: 'hook_style', label: '3-Second Hook Style', type: 'select', options: ['Shocking Fact / Curiosity', 'Problem Everyone Faces', 'Controversial Statement', 'Behind-the-Scenes Secret'], defaultValue: 'Shocking Fact / Curiosity', required: false },
    ],
    defaultModelId: 'mod_gpt_4o',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_youtube_description',
    categoryId: 'pcat_marketing',
    title: 'YouTube Video Description & SEO',
    slug: 'youtube-video-description',
    description: 'Generates SEO-rich YouTube descriptions complete with timestamps, keyword optimization, and links.',
    systemPrompt: 'You are a YouTube SEO producer. Maximize discoverability and click-throughs in search and suggested videos.',
    promptTemplate: 'Create an optimized YouTube video description for:\n\nVideo Title: {{title}}\nVideo Summary: {{summary}}\nTimestamps/Key Sections: {{timestamps}}\nImportant Links/Socials: {{links}}',
    inputSchema: [
      { name: 'title', label: 'YouTube Video Title', type: 'text', placeholder: 'e.g. Building an AI SaaS in 2026: Cloudflare Workers Full Guide', required: true },
      { name: 'summary', label: 'Video Summary', type: 'textarea', placeholder: 'e.g. In this tutorial we build a full stack edge application...', required: true },
      { name: 'timestamps', label: 'Timestamps or Agenda', type: 'textarea', placeholder: '0:00 Intro\n2:15 Architecture\n7:30 Live Demo', required: false },
      { name: 'links', label: 'Links to Include', type: 'textarea', placeholder: 'GitHub repo, documentation, social links', required: false },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_youtube_title',
    categoryId: 'pcat_marketing',
    title: 'YouTube Click-Worthy Titles',
    slug: 'youtube-video-title',
    description: 'Suggests 10 high-CTR YouTube titles balancing search volume and curiosity-driven packaging.',
    systemPrompt: 'You are a YouTube algorithm specialist. Generate high-CTR titles without becoming deceptive clickbait.',
    promptTemplate: 'Generate 10 compelling YouTube titles for a video about:\n\nContent: {{content}}\nPrimary Keyword: {{keyword}}',
    inputSchema: [
      { name: 'content', label: 'Video Content or Topic', type: 'textarea', placeholder: 'e.g. How we scaled from 0 to 1M requests on serverless SQLite', required: true },
      { name: 'keyword', label: 'Focus Keyword', type: 'text', placeholder: 'e.g. Cloudflare D1 tutorial', required: false },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },

  // =========================================================================
  // GROUP 3: Email Marketing (6 Templates)
  // =========================================================================
  {
    id: 'ptpl_cold_outreach_email',
    categoryId: 'pcat_email',
    title: 'B2B Cold Outreach Email',
    slug: 'cold-outreach-email',
    description: 'Drafts concise, highly personalized cold emails that bypass spam and prompt genuine replies.',
    systemPrompt: 'You are an enterprise sales leader. Write short (under 125 words), personalized emails that respect the recipient’s time.',
    promptTemplate: 'Write a personalized B2B cold email to:\n\nProspect Title: {{prospect_title}}\nProspect Company/Industry: {{prospect_company}}\nPain Point: {{pain_point}}\nOur Solution: {{solution}}\nSoft Call to Action: {{cta | default: "Open to exploring if this makes sense for your team?"}}',
    inputSchema: [
      { name: 'prospect_title', label: 'Prospect Title / Role', type: 'text', placeholder: 'e.g. VP of Product', required: true },
      { name: 'prospect_company', label: 'Company or Industry', type: 'text', placeholder: 'e.g. E-Commerce Retailer', required: true },
      { name: 'pain_point', label: 'Specific Pain Point', type: 'text', placeholder: 'e.g. Manual copywriting is slowing down product catalog updates', required: true },
      { name: 'solution', label: 'Our Value Proposition', type: 'text', placeholder: 'e.g. AI-automated catalog generation in 20 languages with 99% accuracy', required: true },
      { name: 'cta', label: 'Call to Action', type: 'text', defaultValue: 'Open to exploring if this makes sense for your team?', required: false },
    ],
    defaultModelId: 'mod_gpt_4o',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_email_subject_lines',
    categoryId: 'pcat_email',
    title: 'High Open-Rate Subject Lines',
    slug: 'email-subject-lines',
    description: 'Provides 10 subject line variations engineered to maximize email open rates.',
    systemPrompt: 'You are an email deliverability and open-rate specialist. Generate subject lines under 60 characters.',
    promptTemplate: 'Generate 10 high open-rate email subject lines for:\n\nEmail Topic: {{topic}}\nTarget Audience: {{audience}}\nAngle: {{angle | default: "Curiosity & Urgency"}}',
    inputSchema: [
      { name: 'topic', label: 'Email Content / Offer', type: 'text', placeholder: 'e.g. Announcing new AI Article Wizard launch', required: true },
      { name: 'audience', label: 'Audience', type: 'text', placeholder: 'e.g. Existing Customers', required: false },
      { name: 'angle', label: 'Subject Line Angle', type: 'select', options: ['Curiosity & Urgency', 'Personalized / Low-Key', 'Straightforward Benefit', 'Question-based'], defaultValue: 'Curiosity & Urgency', required: false },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_customer_newsletter',
    categoryId: 'pcat_email',
    title: 'Customer Newsletter',
    slug: 'customer-newsletter',
    description: 'Structures an informative and friendly weekly newsletter with updates, tips, and product news.',
    systemPrompt: 'You are an engaging newsletter editor. Balance value, product updates, and community highlights seamlessly.',
    promptTemplate: 'Draft a customer newsletter covering:\n\nHeadline/Theme: {{theme}}\nKey Updates/News: {{updates}}\nTip of the Week: {{tip}}\nCall to Action: {{cta}}',
    inputSchema: [
      { name: 'theme', label: 'Newsletter Theme or Subject', type: 'text', placeholder: 'e.g. Issue #42: Supercharging Your Edge Latency', required: true },
      { name: 'updates', label: 'Product News & Updates', type: 'textarea', placeholder: 'e.g. Released 35 new templates, lowered prices by 20%', required: true },
      { name: 'tip', label: 'Educational Tip of the Week', type: 'textarea', placeholder: 'e.g. How to use prompt injection sanitization in production', required: false },
      { name: 'cta', label: 'Newsletter CTA', type: 'text', placeholder: 'e.g. Check out the new dashboard', required: false },
    ],
    defaultModelId: 'mod_gpt_4o',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_welcome_email',
    categoryId: 'pcat_email',
    title: 'New Member Welcome Email',
    slug: 'welcome-email',
    description: 'Greets new users with warm enthusiasm, guides initial onboarding, and builds immediate rapport.',
    systemPrompt: 'You are a customer success copywriter. Make onboarding clear, delightful, and non-overwhelming.',
    promptTemplate: 'Write a warm welcome email for new signups of {{app_name}}:\n\nCore Value Proposition: {{value_prop}}\nFirst Step to Take: {{first_step}}\nFounder/Sender Name: {{sender_name | default: "The Team"}}',
    inputSchema: [
      { name: 'app_name', label: 'Application / Platform Name', type: 'text', defaultValue: 'Sophia Platform', required: true },
      { name: 'value_prop', label: 'Core Value to Highlight', type: 'text', placeholder: 'e.g. Creating lightning-fast AI articles without credit card upfront', required: true },
      { name: 'first_step', label: 'Recommended First Step', type: 'text', placeholder: 'e.g. Pick a template from the dashboard and generate your first article', required: true },
      { name: 'sender_name', label: 'Sender Name or Sign-off', type: 'text', defaultValue: 'The Team', required: false },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_follow_up_email',
    categoryId: 'pcat_email',
    title: 'Polite Sales Follow-Up Email',
    slug: 'follow-up-email',
    description: 'Follows up on unread emails or unanswered proposals with grace and refreshed value.',
    systemPrompt: 'You are a respectful B2B relationship manager. Re-engage prospects with low pressure and clear value.',
    promptTemplate: 'Write a follow-up email (Sequence #{{sequence_number | default: 2}}) for:\n\nPrevious Context: {{previous_context}}\nAdditional Value Added: {{new_value}}\nClear Question/Ask: {{ask}}',
    inputSchema: [
      { name: 'previous_context', label: 'Previous Message Context', type: 'text', placeholder: 'e.g. Sent product demo link 3 days ago', required: true },
      { name: 'new_value', label: 'New Value / Resource to Share', type: 'text', placeholder: 'e.g. Attached a case study showing 40% cost reduction', required: false },
      { name: 'ask', label: 'Call to Action / Question', type: 'text', placeholder: 'e.g. Would Thursday 2 PM work for a 10-minute recap?', required: true },
      { name: 'sequence_number', label: 'Follow-Up Sequence Number', type: 'number', defaultValue: 2, required: false },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_cart_abandonment',
    categoryId: 'pcat_email',
    title: 'Cart Abandonment Recovery',
    slug: 'cart-abandonment-email',
    description: 'Re-engages shoppers who left items in their cart, overcoming hesitation with timely incentives.',
    systemPrompt: 'You are an e-commerce direct response marketer. Recover lost checkouts through urgency and reassurance.',
    promptTemplate: 'Write an abandoned cart recovery email for:\n\nStore Name: {{store_name}}\nProduct in Cart: {{product_name}}\nSpecial Offer / Discount: {{discount | default: "Free shipping for the next 24 hours"}}\nSupport / Guarantee: {{guarantee | default: "30-day money-back guarantee"}}',
    inputSchema: [
      { name: 'store_name', label: 'Store Name', type: 'text', placeholder: 'e.g. Sophia Tech Gear', required: true },
      { name: 'product_name', label: 'Item in Cart', type: 'text', placeholder: 'e.g. Pro Developer Annual Subscription', required: true },
      { name: 'discount', label: 'Discount or Incentive', type: 'text', defaultValue: 'Free shipping for the next 24 hours', required: false },
      { name: 'guarantee', label: 'Trust Guarantee', type: 'text', defaultValue: '30-day money-back guarantee', required: false },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },

  // =========================================================================
  // GROUP 4: E-Commerce & Product Copy (5 Templates)
  // =========================================================================
  {
    id: 'ptpl_amazon_description',
    categoryId: 'pcat_ecommerce',
    title: 'Amazon Product Description',
    slug: 'amazon-product-description',
    description: 'Crafts an engaging, Amazon-compliant product description emphasizing benefits, specs, and trust.',
    systemPrompt: 'You are an Amazon FBA copywriting expert. Format descriptions cleanly with bullet points, benefits, and specifications.',
    promptTemplate: 'Write an Amazon product listing description for:\n\nProduct Name: {{product_name}}\nKey Features: {{features}}\nTarget User: {{target_user}}\nWarranty / Guarantee: {{warranty | default: "1-Year Warranty & 100% Satisfaction Guarantee"}}',
    inputSchema: [
      { name: 'product_name', label: 'Product Name', type: 'text', placeholder: 'e.g. Ergonomic Mechanical Keyboard for Developers', required: true },
      { name: 'features', label: 'Key Features & Materials', type: 'textarea', placeholder: 'e.g. Hot-swappable switches, wireless bluetooth, RGB backlight', required: true },
      { name: 'target_user', label: 'Target User', type: 'text', placeholder: 'e.g. Programmers and remote workers', required: false },
      { name: 'warranty', label: 'Warranty Policy', type: 'text', defaultValue: '1-Year Warranty & 100% Satisfaction Guarantee', required: false },
    ],
    defaultModelId: 'mod_gpt_4o',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_product_bullets',
    categoryId: 'pcat_ecommerce',
    title: 'Amazon 5 Feature Bullets',
    slug: 'product-features-bullets',
    description: 'Writes 5 high-impact bullet points adhering to Amazon listing standards (Title in CAPS + Benefit).',
    systemPrompt: 'You are an Amazon listing copywriter. Format each of the 5 bullet points with a CAPTALIZED BENEFIT HEADER followed by clear explanations.',
    promptTemplate: 'Write 5 Amazon feature bullets for:\n\nProduct: {{product}}\nKey Specs: {{specs}}',
    inputSchema: [
      { name: 'product', label: 'Product Title', type: 'text', placeholder: 'e.g. Noise-Canceling Wireless Earbuds', required: true },
      { name: 'specs', label: 'Technical Specifications & Features', type: 'textarea', placeholder: 'e.g. 40h battery, IPX7 waterproof, ANC active noise cancellation', required: true },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_review_responder',
    categoryId: 'pcat_ecommerce',
    title: 'Customer Review Responder',
    slug: 'review-responder',
    description: 'Composes polite, empathetic, and professional public responses to both positive and negative customer reviews.',
    systemPrompt: 'You are a Customer Experience Director. Maintain gratitude for positive feedback and de-escalate negative feedback with empathy and solutions.',
    promptTemplate: 'Write a professional response to this customer review:\n\nReview Rating: {{rating | default: "5 stars"}}\nCustomer Review:\n"{{review_text}}"\n\nCompany Resolution/Offer (if negative): {{resolution}}',
    inputSchema: [
      { name: 'review_text', label: 'Customer Review Text', type: 'textarea', placeholder: 'Paste customer review here...', required: true },
      { name: 'rating', label: 'Star Rating', type: 'select', options: ['5 stars', '4 stars', '3 stars', '2 stars', '1 star'], defaultValue: '5 stars', required: false },
      { name: 'resolution', label: 'Proposed Solution / Contact Info', type: 'text', placeholder: 'e.g. Contact support@store.com for immediate replacement', required: false },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_product_title',
    categoryId: 'pcat_ecommerce',
    title: 'SEO Product Title Generator',
    slug: 'product-title-generator',
    description: 'Creates optimized e-commerce product titles combining Brand, Model, Material, Size, and Key USP.',
    systemPrompt: 'You are an e-commerce SEO specialist. Craft titles following the standard format: Brand + Model + Feature + Color/Size.',
    promptTemplate: 'Generate 5 SEO-optimized product titles for:\n\nBrand: {{brand}}\nProduct Type: {{type}}\nKey Attributes: {{attributes}}',
    inputSchema: [
      { name: 'brand', label: 'Brand Name', type: 'text', placeholder: 'e.g. SophiaCraft', required: true },
      { name: 'type', label: 'Product Type', type: 'text', placeholder: 'e.g. Leather Laptop Sleeve', required: true },
      { name: 'attributes', label: 'Size, Color, Material, Features', type: 'text', placeholder: 'e.g. 15-inch, Brown, Full-grain leather, Water-resistant', required: true },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_faq_generator',
    categoryId: 'pcat_ecommerce',
    title: 'Product & Service FAQ Generator',
    slug: 'faq-generator',
    description: 'Anticipates customer objections and generates a comprehensive FAQ section with clear answers.',
    systemPrompt: 'You are a Conversion Rate Optimization (CRO) expert. Answer common buyer objections clearly to eliminate hesitation.',
    promptTemplate: 'Generate {{faq_count | default: 6}} FAQs and answers for:\n\nProduct or Service: {{product_name}}\nDescription: {{description}}\nKey Policies: {{policies | default: "Free 30-day returns, worldwide shipping, 24/7 support"}}',
    inputSchema: [
      { name: 'product_name', label: 'Product or Service Name', type: 'text', placeholder: 'e.g. Sophia Enterprise API', required: true },
      { name: 'description', label: 'Description & Features', type: 'textarea', placeholder: 'e.g. Edge computing AI API with zero egress fees and automated fallbacks', required: true },
      { name: 'policies', label: 'Shipping, Refund & Support Policies', type: 'text', defaultValue: 'Free 30-day returns, worldwide shipping, 24/7 support', required: false },
      { name: 'faq_count', label: 'Number of FAQs', type: 'number', defaultValue: 6, required: false },
    ],
    defaultModelId: 'mod_gpt_4o',
    isActive: true,
    isPremium: false,
  },

  // =========================================================================
  // GROUP 5: Coding & Developer Tools (6 Templates)
  // =========================================================================
  {
    id: 'ptpl_code_explainer',
    categoryId: 'pcat_coding',
    title: 'Code Explainer & Tutor',
    slug: 'code-explainer',
    description: 'Breaks down complex source code, algorithms, and regex into digestible, plain-English explanations.',
    systemPrompt: 'You are a Principal Software Engineer and Computer Science educator. Explain code clearly with diagrams and time/space complexity.',
    promptTemplate: 'Explain the following {{language | default: "code"}} snippet in plain, easy-to-understand terms:\n\n```{{language | default: "text"}}\n{{code}}\n```\n\nDetail Level: {{detail_level | default: "Comprehensive breakdown with Big-O analysis"}}',
    inputSchema: [
      { name: 'code', label: 'Source Code to Explain', type: 'textarea', placeholder: 'Paste your code here...', required: true },
      { name: 'language', label: 'Language', type: 'text', defaultValue: 'typescript', required: false },
      { name: 'detail_level', label: 'Detail Level', type: 'select', options: ['Quick summary for beginners', 'Comprehensive breakdown with Big-O analysis', 'Line-by-line walkthrough'], defaultValue: 'Comprehensive breakdown with Big-O analysis', required: false },
    ],
    defaultModelId: 'mod_claude_35_sonnet',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_sql_builder',
    categoryId: 'pcat_coding',
    title: 'Natural Language SQL Query Builder',
    slug: 'sql-query-builder',
    description: 'Translates natural English requests into optimized, injection-safe SQL queries.',
    systemPrompt: 'You are a Database Architect and Performance Tuning expert. Write production-grade, indexed SQL queries.',
    promptTemplate: 'Convert this request into an optimized {{dialect | default: "SQLite"}} SQL query:\n\nSchema Context:\n{{schema}}\n\nRequest:\n{{query_request}}',
    inputSchema: [
      { name: 'query_request', label: 'What do you want to query?', type: 'textarea', placeholder: 'e.g. Find top 10 users by credit consumption in the last 30 days', required: true },
      { name: 'schema', label: 'Relevant Tables & Columns', type: 'textarea', placeholder: 'e.g. users (id, name), credit_ledger_entries (user_id, amount, created_at)', required: false },
      { name: 'dialect', label: 'SQL Dialect', type: 'select', options: ['SQLite (Cloudflare D1)', 'PostgreSQL', 'MySQL', 'Snowflake'], defaultValue: 'SQLite (Cloudflare D1)', required: false },
    ],
    defaultModelId: 'mod_claude_35_sonnet',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_regex_generator',
    categoryId: 'pcat_coding',
    title: 'RegEx Generator & Explainer',
    slug: 'regex-generator',
    description: 'Creates regular expressions based on your pattern matching requirements and explains every token.',
    systemPrompt: 'You are a Regular Expressions master. Output the regex clearly with test cases, boundary explanations, and edge-case handling.',
    promptTemplate: 'Create a regular expression to match:\n\nPattern Goal: {{goal}}\nLanguage Flavor: {{flavor | default: "JavaScript / TypeScript (ECMAScript)"}}\nExamples of matches: {{matches}}\nExamples of non-matches: {{non_matches}}',
    inputSchema: [
      { name: 'goal', label: 'What should this Regex match?', type: 'text', placeholder: 'e.g. Strong password with 8+ chars, 1 uppercase, 1 special char', required: true },
      { name: 'matches', label: 'Examples that SHOULD match', type: 'text', placeholder: 'e.g. Pass123!@, Strong#99', required: false },
      { name: 'non_matches', label: 'Examples that should NOT match', type: 'text', placeholder: 'e.g. pass, 12345678, NoSpecialChar1', required: false },
      { name: 'flavor', label: 'Regex Flavor', type: 'select', options: ['JavaScript / TypeScript (ECMAScript)', 'Python (re)', 'PCRE / PHP', 'Go (regexp)'], defaultValue: 'JavaScript / TypeScript (ECMAScript)', required: false },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_git_commit_message',
    categoryId: 'pcat_coding',
    title: 'Conventional Git Commit Message',
    slug: 'git-commit-message',
    description: 'Transforms git diffs or feature notes into structured Conventional Commits (feat, fix, docs, refactor).',
    systemPrompt: 'You are a DevOps lead adhering strictly to the Conventional Commits 1.0.0 specification.',
    promptTemplate: 'Generate conventional commit messages for the following changes:\n\nDiff / Description of Changes:\n{{diff}}\n\nScope (optional): {{scope}}',
    inputSchema: [
      { name: 'diff', label: 'Git Diff or Summary of Changes', type: 'textarea', placeholder: 'e.g. Added JWT token refresh endpoint and unit tests', required: true },
      { name: 'scope', label: 'Optional Scope', type: 'text', placeholder: 'e.g. auth, billing, core-ai', required: false },
    ],
    defaultModelId: 'mod_gpt_4o_mini',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_code_refactor_solid',
    categoryId: 'pcat_coding',
    title: 'Code Refactor & Performance Optimization',
    slug: 'code-refactor',
    description: 'Refactors code for SOLID principles, strict type safety, modular architecture, and maximum runtime performance.',
    systemPrompt: 'You are a Principal Software Architect. Refactor code for optimal performance, strict type safety, zero redundancy, and clean maintainability.',
    promptTemplate: 'Please refactor the following {{language | default: "typescript"}} code:\n\n```{{language | default: "typescript"}}\n{{code}}\n```\n\nSpecific Goals: {{goals | default: "Eliminate any types, apply SOLID principles, optimize runtime complexity"}}',
    inputSchema: [
      { name: 'code', label: 'Source Code to Refactor', type: 'textarea', placeholder: 'Paste code here...', required: true },
      { name: 'language', label: 'Programming Language', type: 'text', defaultValue: 'typescript', required: false },
      { name: 'goals', label: 'Refactoring Goals', type: 'text', defaultValue: 'Eliminate any types, apply SOLID principles, optimize runtime complexity', required: false },
    ],
    defaultModelId: 'mod_claude_35_sonnet',
    isActive: true,
    isPremium: false,
  },
  {
    id: 'ptpl_api_docs_generator',
    categoryId: 'pcat_coding',
    title: 'API Documentation & OpenAPI Specs',
    slug: 'api-documentation',
    description: 'Transforms backend route handlers or payload structures into clean OpenAPI 3.1 specifications and developer guides.',
    systemPrompt: 'You are a Developer Relations Engineer. Write clear, developer-friendly API documentation with request/response examples and status codes.',
    promptTemplate: 'Generate developer API documentation for this endpoint:\n\nMethod & Path: {{method}} {{path}}\nDescription: {{description}}\nRequest Payload / Params:\n{{request_body}}\n\nResponse Example:\n{{response_example}}',
    inputSchema: [
      { name: 'method', label: 'HTTP Method', type: 'select', options: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'], defaultValue: 'POST', required: true },
      { name: 'path', label: 'Endpoint Path', type: 'text', placeholder: 'e.g. /api/v1/text/completions/stream', required: true },
      { name: 'description', label: 'Endpoint Purpose', type: 'text', placeholder: 'e.g. Streams AI completions via SSE', required: true },
      { name: 'request_body', label: 'Request Body / Parameters', type: 'textarea', placeholder: '{ model: string, messages: array }', required: false },
      { name: 'response_example', label: 'Response Example', type: 'textarea', placeholder: '{ status: "success", data: ... }', required: false },
    ],
    defaultModelId: 'mod_gpt_4o',
    isActive: true,
    isPremium: false,
  },
];
